<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Draggable from 'dashboard/components/widgets/webphone/Draggable.vue';
import { useWacallsCall } from 'dashboard/composables/useWacallsCall';
import { useVoiceCallStatus } from 'dashboard/composables/useVoiceCallStatus';
import { startRingback, stopRing } from 'dashboard/helper/callRingtone';

const { t } = useI18n();
const store = useStore();

// --- store ---
const call = computed(() => store.getters['wacallsCall/getCall']);
const uiFlags = computed(() => store.getters['wacallsCall/getUIFlags']);

// --- composable (OWNS AudioContext + RTCPeerConnection) ---
const { toggleMute, hangup, isMuted } = useWacallsCall();

// --- call timer ---
const elapsedSeconds = ref(0);
const timer = ref(null);

const formattedDuration = computed(() => {
  const m = Math.floor(elapsedSeconds.value / 60);
  const s = elapsedSeconds.value % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
});

function stopTimer() {
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
  elapsedSeconds.value = 0;
}

function startTimer() {
  stopTimer();
  elapsedSeconds.value = 0;
  timer.value = setInterval(() => {
    elapsedSeconds.value += 1;
  }, 1000);
}

watch(
  () => call.value?.status,
  status => {
    if (status === 'active') {
      stopRing();
      startTimer();
    } else if (!status || status === 'ended') {
      stopRing();
      stopTimer();
    } else {
      // calling / connecting / ringing — play the ringback tone
      startRingback();
    }
  },
  { immediate: true }
);

// --- status mapping for useVoiceCallStatus ---
const mappedStatus = computed(() => {
  const s = call.value?.status;
  if (s === 'active') return 'in-progress';
  if (s === 'ended') return 'completed';
  return 'ringing';
});

const { bubbleIconBg } = useVoiceCallStatus(mappedStatus, 'outbound');

// --- status label ---
const statusLabel = computed(() => {
  const s = call.value?.status;
  if (s === 'active') return t('CONVERSATION.WACALLS_CALL.IN_CALL');
  if (s === 'ended') return t('CONVERSATION.WACALLS_CALL.ENDED');
  return t('CONVERSATION.WACALLS_CALL.CALLING');
});

// --- hang up ---
async function onHangup() {
  const { conversationId } = call.value || {};
  try {
    await hangup(conversationId);
  } finally {
    store.dispatch('wacallsCall/reset');
  }
}

onUnmounted(() => {
  stopRing();
  stopTimer();
});
</script>

<template>
  <Draggable :initial-x="50" :initial-y="50">
    <!-- Active call panel -->
    <div
      v-if="uiFlags.isOpen"
      data-testid="wacalls-call-panel"
      class="flex flex-col h-[280px] w-[250px] bg-white dark:bg-slate-900 opacity-95 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg select-none cursor-grab"
    >
      <!-- Contact + status -->
      <div
        class="flex flex-col items-center justify-center flex-1 gap-1 px-4 pt-6"
      >
        <div
          class="w-10 h-10 rounded-full flex items-center justify-center mb-2"
          :class="bubbleIconBg"
        >
          <span class="i-ri-phone-fill text-white text-base" />
        </div>
        <p
          class="text-base font-semibold text-slate-800 dark:text-slate-100 m-0 text-center truncate max-w-full"
        >
          {{ call.contactName || call.phone }}
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400 m-0 text-center">
          {{ statusLabel }}
        </p>
        <p
          v-if="call.status === 'active'"
          class="text-lg font-mono font-medium text-slate-800 dark:text-slate-100 m-0 text-center tabular-nums"
        >
          {{ formattedDuration }}
        </p>
      </div>

      <!-- Controls -->
      <div class="flex justify-center items-center gap-5 pb-6">
        <!-- Mute / Unmute -->
        <button
          v-tooltip="
            isMuted
              ? $t('CONVERSATION.WACALLS_CALL.UNMUTE')
              : $t('CONVERSATION.WACALLS_CALL.MUTE')
          "
          data-testid="wacalls-mute-btn"
          class="flex items-center justify-center w-10 h-10 rounded-full transition-colors"
          :class="
            isMuted
              ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300'
              : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
          "
          @click="toggleMute"
        >
          <span
            class="text-base"
            :class="isMuted ? 'i-ri-mic-off-line' : 'i-ri-mic-line'"
          />
        </button>

        <!-- Hang up -->
        <button
          v-tooltip="$t('CONVERSATION.WACALLS_CALL.HANG_UP')"
          data-testid="wacalls-hangup-btn"
          class="flex items-center justify-center w-10 h-10 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
          @click="onHangup"
        >
          <span class="text-base i-lucide-phone-off" />
        </button>
      </div>
    </div>
  </Draggable>
</template>
